import React, { useState, useEffect, useCallback } from 'react'
import {
  Stack,
  Text,
  Button,
  SimpleModal,
  FormControl,
  Input,
  DateChooser,
  Alert,
  UserChooser
} from '@/components/lib'
import api from '@/api'
import utils from '@/utils/utils'
import useStore from '@/store'

export function defaultMeetingName (participant) {
  return participant?.id ? `Meeting with ${participant.id}` : 'Meeting'
}

/**
 * Create or edit a participant video meeting (Whereby). Same behaviour as the Meetings tab modal.
 */
export default function ParticipantMeetingEditor ({
  isOpen,
  onClose,
  trialId,
  participantId,
  participant,
  /** Existing meeting to edit; omit or null for create. */
  meeting,
  /** When creating, optional default start (e.g. calendar day + time). */
  defaultStartAt,
  onSaved
}) {
  const { getTrialUsers } = useStore()
  const trialUsers = getTrialUsers(trialId)

  const [name, setName] = useState('')
  const [startAt, setStartAt] = useState(null)
  const [personnelIds, setPersonnelIds] = useState([])
  const [saving, setSaving] = useState(false)

  const isEdit = !!(meeting && meeting._id)

  useEffect(() => {
    if (!isOpen) return
    if (isEdit) {
      setName(meeting.name || '')
      setPersonnelIds(meeting.personnel || [])
      setStartAt(null)
    } else {
      setName('')
      setPersonnelIds([])
      setStartAt(defaultStartAt ?? null)
    }
  }, [isOpen, isEdit, meeting, defaultStartAt])

  const handleClose = useCallback(() => {
    if (!saving) onClose?.()
  }, [saving, onClose])

  function handleSave () {
    if (isEdit) {
      setSaving(true)
      api.participants.updateMeeting(
        trialId,
        participantId,
        meeting._id,
        {
          name: (name || '').trim() || defaultMeetingName(participant),
          personnel: personnelIds
        },
        (m) => {
          setSaving(false)
          utils.toast('success', 'Meeting updated')
          onSaved?.(m)
          onClose?.()
        },
        (err) => {
          setSaving(false)
          utils.toast('error', err.message)
        }
      )
      return
    }
    if (!startAt) {
      utils.toast('error', 'Please set a start date and time')
      return
    }
    setSaving(true)
    const data = {
      startAt: startAt instanceof Date ? startAt.toISOString() : startAt,
      name: (name || '').trim() || defaultMeetingName(participant),
      personnel: personnelIds
    }
    api.participants.createMeeting(
      trialId,
      participantId,
      data,
      (m) => {
        setSaving(false)
        utils.toast('success', 'Meeting created')
        onSaved?.(m)
        onClose?.()
      },
      (err) => {
        setSaving(false)
        utils.toast('error', err.message)
      }
    )
  }

  function onPersonnelChosen (value) {
    setPersonnelIds(
      Array.isArray(value)
        ? value.map((v) => (typeof v === 'object' && v?._id ? v._id : v))
        : []
    )
  }

  return (
    <SimpleModal
      size='sm'
      isOpen={isOpen}
      onClose={handleClose}
      header={isEdit ? 'Edit meeting' : 'Create video meeting'}
      body={
        <Stack gap={4}>
          <FormControl label='Name'>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={defaultMeetingName(participant)}
            />
          </FormControl>
          {isEdit
            ? (
              <FormControl label='Start at'>
                <Text fontSize='sm' color='fg.subtle'>
                  {utils.formatDateTime(meeting.startAt, { withTimezone: true })} (cannot be changed)
                </Text>
              </FormControl>
              )
            : (
              <FormControl label='Start at' required helperText='The room will be valid for 24 hours from the start time.'>
                <Stack direction='row' gap={2} alignItems='center'>
                  <DateChooser
                    withTime
                    value={startAt}
                    onChange={(date) => setStartAt(date)}
                    placeholder='Select date and time'
                  />
                  <Text fontSize='sm' color='fg.subtle'>{Intl.DateTimeFormat().resolvedOptions().timeZone}</Text>
                </Stack>
              </FormControl>
              )}
          <FormControl label='Personnel' helperText='These users will receive reminder emails about the meeting.'>
            <UserChooser
              multiple
              placeholder='Select who will join this meeting'
              users={trialUsers}
              selectedUsers={personnelIds}
              onChosen={onPersonnelChosen}
            />
          </FormControl>
          {!isEdit && (
            <Alert>
              If possible, Trialflare will send the participant a message containing the details of the meeting.
            </Alert>
          )}
        </Stack>
      }
      footer={
        <Stack direction='row' gap={2} justifyContent='flex-end'>
          <Button variant='ghost' onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button colorPalette='primary' onClick={handleSave} loading={saving}>
            {isEdit ? 'Save' : 'Create meeting'}
          </Button>
        </Stack>
      }
    />
  )
}
